"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { noticeError } from "@utils/monitoring";
import { trackSystemError } from "@utils/exceptionTracker";
import styles from "./AppErrorFallback.module.scss";

const CARS_ROUTE = "/cars/";
const REDIRECT_SECONDS = 6;

interface AppErrorFallbackProps {
  error?: Error & { digest?: string };
  source?: "route" | "global";
}

export default function AppErrorFallback({
  error,
  source = "route",
}: AppErrorFallbackProps) {
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  const pathname = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return window.location.pathname;
  }, []);

  useEffect(() => {
    if (!error) {
      return;
    }

    noticeError({
      err: error,
      customAttributes: {
        errorBoundary: source,
        digest: error.digest,
        pathname,
      },
    });

    trackSystemError("application_error_boundary", "application_error", {
      source,
      digest: error.digest,
      pathname,
    }).catch(() => undefined);
  }, [error, pathname, source]);

  useEffect(() => {
    const redirectTimer = window.setTimeout(() => {
      window.location.assign(CARS_ROUTE);
    }, REDIRECT_SECONDS * 1000);

    const countdownTimer = window.setInterval(() => {
      setSecondsLeft((currentSeconds) => Math.max(currentSeconds - 1, 0));
    }, 1000);

    return () => {
      window.clearTimeout(redirectTimer);
      window.clearInterval(countdownTimer);
    };
  }, []);

  const handleBrowseCars = () => {
    window.location.assign(CARS_ROUTE);
  };

  return (
    <main className={styles.container}>
      <section className={styles.content} aria-labelledby="app-error-title">
        <div className={styles.iconContainer}>
          <Image
            src={`${assetLoader()}/images/nocontent.svg`}
            alt=""
            width={361}
            height={181}
            priority
          />
        </div>

        <div className={styles.copy}>
          <h1 id="app-error-title" className={styles.title}>
            Something went wrong
          </h1>
          <p className={styles.description}>
            We are fixing it. Meanwhile, you can browse other cars.
          </p>
          <p className={styles.redirectText}>
            Redirecting to cars in {secondsLeft} seconds.
          </p>
        </div>

        <div className={styles.actions}>
          <button className={styles.primaryButton} onClick={handleBrowseCars}>
            Browse Cars
          </button>
        </div>
      </section>
    </main>
  );
}
